import React from 'react';
import { usePermissions, PermissionContext } from '@/hooks/usePermissions';
import { Loader2 } from 'lucide-react';

// Boolean permission keys from PermissionContext
type BooleanPermissionKey = {
  [K in keyof PermissionContext]: PermissionContext[K] extends boolean ? K : never;
}[keyof PermissionContext];

interface RequirePermissionProps {
  // Permission can be:
  // - A string key for boolean permissions (e.g., 'canManageUsers')
  // - A function that receives the context and returns boolean
  permission: BooleanPermissionKey | ((ctx: PermissionContext) => boolean);

  // What to show when permission is denied (defaults to nothing)
  fallback?: React.ReactNode;

  // What to show while loading permissions (defaults to nothing)
  loadingFallback?: React.ReactNode;

  // Whether to show a loading spinner while permissions load
  showLoading?: boolean;

  children: React.ReactNode;
}

export function RequirePermission({
  permission,
  fallback = null,
  loadingFallback,
  showLoading = false,
  children,
}: RequirePermissionProps) {
  const permissions = usePermissions();

  // Show loading state if permissions are still loading
  if (permissions.loading) {
    if (loadingFallback) {
      return <>{loadingFallback}</>;
    }
    if (showLoading) {
      return (
        <div className="flex items-center justify-center p-4">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      );
    }
    return null;
  }

  // Evaluate permission
  const hasPermission = typeof permission === 'function'
    ? permission(permissions)
    : permissions[permission];

  // Show fallback if permission denied
  if (!hasPermission) {
    return <>{fallback}</>;
  }

  // Render children if permission granted
  return <>{children}</>;
}

// Higher-order component version for class components or route wrapping
export function withPermission<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  permission: BooleanPermissionKey | ((ctx: PermissionContext) => boolean),
  FallbackComponent?: React.ComponentType
) {
  return function WithPermissionWrapper(props: P) {
    return (
      <RequirePermission
        permission={permission}
        fallback={FallbackComponent ? <FallbackComponent /> : null}
      >
        <WrappedComponent {...props} />
      </RequirePermission>
    );
  };
}

// Hook for checking permissions in component logic
export function useHasPermission(
  permission: BooleanPermissionKey | ((ctx: PermissionContext) => boolean)
): { hasPermission: boolean; loading: boolean } {
  const permissions = usePermissions();

  const hasPermission = typeof permission === 'function'
    ? permission(permissions)
    : permissions[permission];

  return {
    hasPermission: Boolean(hasPermission),
    loading: permissions.loading,
  };
}
